import {
  Input,
  Output,
  EventEmitter,
  Directive,
  OnInit,
  AfterViewInit,
  inject,
  NgZone,
} from '@angular/core';
import { DynamicItemBaseComponent } from '../dynamic-item/dynamic-item.base-component';
import { catchError, filter, map, takeUntil } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
  IDynamicModel,
  IDynamicConflictState,
  IDynamicUpdateOnValueChange,
} from '../../models';
import { noop } from '@ups/xplat/utils';
import {
  DynamicEventBusTypes,
  observeFormReset,
  resolveConflictResolution,
  dynamicUnwrapSelection,
  dynamicWithPick,
  dynamicSameRecord,
} from '../../utils';
import {
  TypeaheadFilterRequest,
  createTypeaheadDataEvent,
  isOwnFilterRequest,
  nextTypeaheadSearchScope,
} from '@ups/xplat/core';
import {
  SelectedItemType,
  deriveSelectedItems,
} from './dynamic-typeahead-selection';
import { dynamicResolveSavedIds } from './dynamic-typeahead-saved-ids';
import { dynamicScopeDefaultQuery } from './dynamic-typeahead-scope-query';
import { DynamicFanOutLoading } from './dynamic-typeahead-fan-out';
import {
  dynamicFanOutUnheard,
  dynamicFindControl,
  dynamicHasFanOutError,
  dynamicObserveFanOutRetry,
  dynamicSetFanOutError,
  dynamicSetLoadingFlag,
} from './dynamic-typeahead-fan-out-gate';
import { liveConflictSourceLabels } from './dynamic-typeahead-conflict-sources';
import {
  dynamicSingleFavorite,
  dynamicFindPrefillMatch,
  dynamicGroupControl,
  dynamicLookupGateEnabled,
  dynamicObserveLookupRetry,
  dynamicSearchParams,
  dynamicSetPrefillError,
} from './dynamic-typeahead-lookup-gate';

@Directive()
export abstract class DynamicTypeaheadBaseComponent
  extends DynamicItemBaseComponent
  implements OnInit, AfterViewInit
{
  // bind this to the api service call in the component implementation
  searchQuery: (...args: Array<unknown>) => Observable<unknown>;

  // allows an id to be shown in the dropdown options
  displayIdPrefix: boolean;
  readonly searchScope = nextTypeaheadSearchScope(); // #34282 picker scope

  /* eslint-disable */
  selectedItem: SelectedItemType;
  /* eslint-enable */
  selectedItems: Array<SelectedItemType>;
  multiSelectValues: Array<SelectedItemType>;
  // each implementation can define a custom filter function to query it's data against
  queryFilter: (query: string, data: unknown) => boolean;

  @Input() isMultiSelect = false;
  @Input() showMultiSelectCheckbox = false;
  // used when a use case desires to explicitly hide a typeahead search input
  @Input() hideSearch: boolean;
  // any typeahead components can provide their own additional custom query params
  @Input() customQueryParams: unknown;

  @Output() loaded: EventEmitter<unknown> = new EventEmitter();
  @Output() valueChange: EventEmitter<unknown> = new EventEmitter<unknown>();
  @Output() valuesChange: EventEmitter<Array<unknown>> = new EventEmitter<
    Array<unknown>
  >();
  @Output() remove: EventEmitter<unknown> = new EventEmitter();
  /** Fired when a remote typeahead search throws. Payload is the error. Empty-success does not emit. */
  @Output() searchFailed: EventEmitter<unknown> = new EventEmitter<unknown>();

  protected ngZone = inject(NgZone);

  // cdr is inherited (protected) from DynamicItemBaseComponent (WI 31903 CD-plumbing).
  private touchedCallback: () => void = noop;
  private changeCallback: (_: unknown) => void = noop;

  private _matchingTargetProperty: string;
  /** Ensures we only trigger initial load for preSelectSingleFavorite once */
  private _preSelectInitialLoadTriggered = false;
  /**
   * Monotonic remote-search sequence. Only the latest search may set or clear
   * `hasSearchError_dynamic`. Late failures from superseded keystrokes still log
   * but must not flip form state while newer results are on screen.
   */
  private _searchSeq = 0;
  /** Bug 36106: sequence of searches that paint a list (carry `extension`); only the latest paints. */
  private _listSeq = 0;

  /**
   * Conflict-resolution provenance: false => current value is "sticky" (user-selected or
   * loaded from a saved record); true => current value came from an auto-default push.
   * Only a sticky value can raise a conflict. Set inside `_setSelectedItem` from its `origin`
   * arg; `writeValue` (saved-record load) never touches it, so loaded values stay sticky.
   */
  private _lastValueWasAutoDefault = false;
  /** Counts picks so a slow fan-out lookup from an older pick is dropped (WI 34965). */
  private _fanOutPick = 0;
  private _fanOutLoading = new DynamicFanOutLoading();
  /** True while a conflict is live (raised, possibly already resolved) — the user may still toggle their choice. */
  private _pendingConflictActive = false;
  /** The default stashed for "Switch to default" — may be null when the new selection has no default (a clear). */
  private _pendingConflictDefault: SelectedItemType | null = null;
  /** The original sticky value at raise time, stashed for "Keep" so the user can switch back after switching. */
  private _pendingConflictCurrent: SelectedItemType | null = null;
  /** Set in ngOnDestroy so a deferred reset queued before teardown bails out. */
  private viewDestroyed = false;
  /** Bug 34978: records returned so far by the per-id multi-select prefill searches. */
  private _prefillFound: Array<object> | null = null;
  private _prefillHadError = false; // WI 35277: a step of the prefill chain failed
  /** Bug 34978: bumped per prefill, so a reply from a superseded chain is dropped. */
  private _prefillChain = 0;
  private _prefillInFlight = false; // a prefill chain has not settled yet

  get value(): SelectedItemType {
    return this.selectedItem;
  }

  set value(v: SelectedItemType) {
    if (v !== this.selectedItem) {
      // Defer to next tick to avoid NG0100 error in Angular 21+
      this.win.setTimeout(() => {
        this.selectedItem = v;
        this.selectedItems = v ? [v] : [];
        this.changeCallback(v);
        this.detectChangesIfAlive();
      });
    }
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.config.options) {
      this.displayIdPrefix = this.config.options.displayIdPrefix;
    }

    this.dynamicService?.itemTargetUpdate$
      .pipe(
        filter(
          (updates) =>
            !!(
              updates.formControlName === this.config.formControlName &&
              updates.options
            )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((updates: IDynamicModel) => {
        if (
          !this.config.readOnly &&
          updates.options.typeahead &&
          updates.options.typeahead.matchTargetProperty
        ) {
          this.isLoading = true;
          this._matchingTargetProperty =
            updates.options.typeahead.matchTargetProperty;
          this.filterChangeItem({ query: updates.value || '' }).then(
            (result) => {
              if (result && Array.isArray(result)) {
                const valueLowerCase = updates.value?.toString()?.toLowerCase();
                const valueMatch = result.find(
                  (item) =>
                    item[this._matchingTargetProperty]
                      ?.toString()
                      ?.toLowerCase() === valueLowerCase
                );

                // Conflict-resolution gate: when this control opted in and currently holds a
                // sticky (user-selected/saved) value that differs from the incoming auto-default,
                // do NOT silently overwrite — prompt, or silently keep, per the configured mode.
                if (this._maybeHandleAutoDefaultConflict(valueMatch, updates)) {
                  this._matchingTargetProperty = null;
                  this.isLoading = false;
                  return;
                }

                if (
                  valueMatch ||
                  updates.options.typeahead.setNullValueIfNotFoundInSearch
                ) {
                  // value is arriving from an auto-default push (not a user action)
                  this._lastValueWasAutoDefault = true;
                  this.ngZone.run(() => {
                    if (valueMatch && this.config?.options?.isMultiSelect) {
                      this._applyMultiSelectAutoDefault(valueMatch);
                      return;
                    }
                    this.updateValue(valueMatch);
                    if (valueMatch) {
                      this.valueChangeItem(valueMatch, 'auto');
                    }
                  });
                } else {
                  this._matchingTargetProperty = null;
                }
              }

              this.isLoading = false;
            }
          );
        }
      });

    // Conflict resolution: the user picked keep / switch-to-default in the label popover.
    this.dynamicService?.conflictUpdate$
      .pipe(
        filter(
          (m) =>
            m.formControlName === this.config.formControlName &&
            m.role === 'target'
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((m) => {
        if (m.type === 'resolve') {
          if (this._pendingConflictActive) {
            // Apply the chosen value. The stash is RETAINED so the user can change their mind
            // (toggle keep<->switch) until the form is saved/cancelled. 'default' may be null (a clear).
            const next =
              m.choice === 'default'
                ? this._pendingConflictDefault
                : this._pendingConflictCurrent;
            // applied as the user's confirmed choice => 'user' origin keeps it sticky afterwards
            this.ngZone.run(() => {
              this.updateValue(next);
              this.valueChangeItem(next, 'user');
            });
          }
          // a choice was made => form is saveable again (gray icon); keep the stash for re-toggling
          this.conflictPending = false;
        } else if (m.type === 'clear') {
          this._pendingConflictActive = false;
          this._pendingConflictDefault = null;
          this._pendingConflictCurrent = null;
          this.conflictPending = false;
        }
      });

    this.eventBus
      .observe(this.eventBus.types.filterTypeahead)
      .pipe(
        filter((r: TypeaheadFilterRequest) => isOwnFilterRequest(r, this)),
        takeUntil(this.destroy$)
      )
      .subscribe((r: TypeaheadFilterRequest) => this.handleFilterRequest(r));

    observeFormReset(this.eventBus, () => this.group)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetSelectedItems();
        this._resetConflictState();
      });

    this.eventBus
      .observe(DynamicEventBusTypes.dynamicFormTotalReset)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this._resetConflictState();
      });

    dynamicObserveLookupRetry(
      this.eventBus,
      this.destroy$,
      () => dynamicGroupControl(this.group, this.config.formControlName),
      () => this.initCheckValue()
    );
    dynamicObserveFanOutRetry(this.eventBus, this.destroy$, () =>
      this._retryFanOuts()
    );
  }

  ngAfterViewInit() {
    this.initCheckValue();

    // When preSelectSingleFavorite is set, trigger one initial load so we can
    // pre-select the one favorite before the user opens the dropdown.
    // Never on edit/reopen: hasNoSelection is present-tense empty; editing is provenance
    // ("was this form saved"). ADO #30156 — favorite overwrite of saved typeahead values.
    if (
      this.shouldPreSelectSingleFavorite() &&
      !this.valueObjectId &&
      this.searchQuery &&
      !this._preSelectInitialLoadTriggered
    ) {
      this._preSelectInitialLoadTriggered = true;
      this.win.setTimeout(() => {
        if (this.hasNoSelection()) {
          this.filterChangeItem({
            query: '',
            extension: { applyData: () => {} },
          });
        }
      }, 150);
    }

    this.loaded.emit(this);
  }

  /**
   * preSelectSingleFavorite is only for new forms. On edit, a present-tense empty control
   * (empty save or unresolved valueObjectId) must not load and stamp a favorite.
   */
  public shouldPreSelectSingleFavorite(): boolean {
    return (
      !!this.config?.options?.preSelectSingleFavorite &&
      this.config?.options?.editing !== true
    );
  }

  override ngOnDestroy() {
    this.viewDestroyed = true;
    if (this.config?.options?.conflictResolution) {
      this.dynamicService?.clearConflict(this.config.formControlName);
    }
    this.conflictPending = false;
    super.ngOnDestroy();
  }

  initCheckValue() {
    // Bug 34978: a new prefill supersedes any chain still in flight.
    this._prefillChain = (this._prefillChain ?? 0) + 1;
    this._prefillFound = null;
    this._prefillHadError = false;
    if (this.valueObjectId) {
      if (
        this.config?.options?.isMultiSelect &&
        this.config?.options?.initWithQuery
      ) {
        // with multiselect, ensure all results return and the full collection is then filtered
        // ensure initWithQuery is a string - can be a number when singular value
        this.multiSelectValues = `${this.config.options.initWithQuery}`.split(
          ','
        );
        if (this.multiSelectValues.length) {
          // enumerate values, querying api for each (to ensure deep search matches found)
          // start with first value
          this.config.options.initWithQuery = this.multiSelectValues[0];
        } else {
          this.config.options.initWithQuery = '';
        }
      }
      // find a match to prefill
      this.findMatchToPrefill(
        this.config?.options?.initWithQuery,
        this.multiSelectValues ? this.multiSelectValues.length === 1 : true
      );
    } else if (this._prefillInFlight) {
      // the chain this one superseded will never settle (it returns early), so settle here
      this._prefillInFlight = false;
      this.isLoading = false;
      dynamicSetPrefillError(
        dynamicGroupControl(this.group, this.config?.formControlName),
        false
      );
    }
  }

  protected singlePreSelectCandidate(data: Array<unknown>): unknown | null {
    return this.shouldPreSelectSingleFavorite()
      ? dynamicSingleFavorite(data)
      : null;
  }

  /** The control an updateOnValueChange entry writes, anywhere in the form (WI 35277). */
  private _fanOutTarget(selectionConfig: IDynamicUpdateOnValueChange) {
    return dynamicFindControl(
      this.group?.root,
      selectionConfig.formControlName
    );
  }

  /** One updateOnValueChange entry: start its lookup and push the result (pick or Retry). */
  private _runFanOut(
    selectionConfig: IDynamicUpdateOnValueChange,
    pick: number
  ) {
    const name = selectionConfig.formControlName as string;
    const target = this._fanOutTarget(selectionConfig);
    // set the loading indicator on the target control as we are already calculating data for it here
    this.dynamicService?.itemTargetUpdate$.next({
      formControlName: name,
      type: 'target-loading',
      value: 1,
    });
    this._fanOutLoading.start(name, pick);
    // WI 35277: nobody heard that push (collapsed section); hold Save on the control itself
    const unheard = () => dynamicFanOutUnheard(target);
    if (unheard()) {
      dynamicSetLoadingFlag(target, true);
    }

    let propertyValuePromise: Promise<string | number>;
    if (selectionConfig.calculatedPropertyValue != null) {
      propertyValuePromise = selectionConfig
        .calculatedPropertyValue(
          this.selectedItem,
          this.config,
          this.dynamicService?.activeForm ?? this.group
        )
        .toPromise();
    } else {
      propertyValuePromise = Promise.resolve(
        this.selectedItem[selectionConfig.property]
      );
    }

    const selected = this.selectedItem;
    propertyValuePromise.then(
      (propertyValue) => {
        const isCurrent = pick === this._fanOutPick;
        // WI 34965: an older pick's late value never overwrites a newer pick's.
        const update = this._fanOutLoading.resolve(
          selectionConfig,
          { pick, isCurrent },
          propertyValue,
          this.config ?? {},
          selected
        );
        if (update) {
          this.dynamicService?.itemTargetUpdate$.next(update);
        }
        if (unheard() && (isCurrent || update)) {
          dynamicSetLoadingFlag(target, false);
        }
        // Fail open (STG 2026-10-08): an unmounted target (Required Fields Only) cannot hear an
        // ANSWER and Retry cannot make it, so it keeps its value; only a FAILED lookup blocks.
        if (isCurrent) {
          dynamicSetFanOutError(target, false);
        }
      },
      (err) => {
        // WI 35277: only a lookup-gated form handles a failed lookup; others are unchanged.
        if (!dynamicLookupGateEnabled(this.group)) throw err;
        const clear = this._fanOutLoading.settle(name, pick);
        if (clear) {
          this.dynamicService?.itemTargetUpdate$.next(clear);
          if (unheard()) {
            dynamicSetLoadingFlag(target, false);
          }
        }
        // the target still holds the previous pick's value: block Save with Retry
        if (pick === this._fanOutPick) {
          dynamicSetFanOutError(target, true);
        }
      }
    );
  }

  /** WI 35277: the host's Retry re-runs each failed fan-out for the current pick. */
  private _retryFanOuts() {
    if (!this.selectedItem) {
      return;
    }
    for (const selectionConfig of this.config?.options?.updateOnValueChange ??
      []) {
      if (dynamicHasFanOutError(this._fanOutTarget(selectionConfig))) {
        this._runFanOut(selectionConfig, this._fanOutPick);
      }
    }
  }

  /**
   * WI 35277 (lookup-gate forms only): the user picked or cleared a value, so the saved value's
   * lookup no longer matters. Supersede any chain in flight and drop its failure, so a failed
   * saved-value lookup cannot keep Save blocked after the user has replaced the value.
   */
  private retirePrefill(): void {
    const control = dynamicGroupControl(
      this.group,
      this.config?.formControlName
    );
    if (!control || !dynamicLookupGateEnabled(control)) {
      return;
    }
    this._prefillChain = (this._prefillChain ?? 0) + 1;
    if (this._prefillInFlight) {
      this._prefillInFlight = false;
      this.isLoading = false;
    }
    dynamicSetPrefillError(control, false);
    dynamicSetFanOutError(control, false);
  }

  findMatchToPrefill(query: string, complete = true) {
    const chain = this._prefillChain;
    this._prefillInFlight = true;
    this.isLoading = true;
    this.filterChangeItem(
      {
        query: query || '',
      },
      this.config.options.valueQueryParamName
    ).then((result) => {
      // a superseded chain (re-prefill, Retry) must not apply its match or settle the
      // loading / error state while the newer chain is still in flight
      if (chain !== this._prefillChain) {
        return;
      }
      this._prefillHadError = this._prefillHadError || this.searchError;
      if (result && Array.isArray(result)) {
        if (this.config?.options?.isMultiSelect) {
          // Bug 34978: gather every per-id search, then keep each saved id, as a placeholder
          // chip when no search returned it, so a reopen and save never drops it.
          this._prefillFound = [...(this._prefillFound ?? []), ...result];
          if (!complete && this.multiSelectValues) {
            this.multiSelectValues.splice(0, 1);
            this.findMatchToPrefill(
              this.multiSelectValues[0],
              this.multiSelectValues.length === 1
            );
            return;
          }
          const ids = this.valueObjectId?.toString()?.split(',') ?? [];
          // A placeholder carries only the id and label fields, not a full record.
          const items = dynamicResolveSavedIds(
            ids,
            this._prefillFound,
            this.config?.valueProperty || 'Id',
            this.config
          ) as unknown as Array<SelectedItemType>;
          this._prefillFound = null;
          this.multiSelectValues = null;
          if (items.length) {
            this.selectedItems = items;
            this.updateValue(this.selectedItems);
          }
        } else {
          const valueMatch = dynamicFindPrefillMatch(
            result,
            this.valueObjectId || this.config?.options?.initWithQuery,
            this.config
          );

          if (valueMatch) {
            // Defer to next tick to avoid NG0100 error in Angular 21+
            this.win.setTimeout(() => {
              this.selectedItem = valueMatch;
              this.updateValue(valueMatch);
              this.detectChangesIfAlive();
            });
          }
        }
      }
      if (complete) {
        dynamicSetPrefillError(
          dynamicGroupControl(this.group, this.config.formControlName),
          this._prefillHadError
        );
        this.isLoading = false;
        this._prefillInFlight = false;
      }
    });
  }

  /** True when the control has no value, so pre-select is allowed. Uses form control + internal state for consistency. Public so mobile modal can pass it when opening. */
  public hasNoSelection(): boolean {
    const control = this.group?.controls?.[this.config?.formControlName];
    const value = control?.value;
    if (this.config?.options?.isMultiSelect) {
      const emptyArray = !value || !Array.isArray(value) || value.length === 0;
      const noInternal = !this.selectedItems || this.selectedItems.length === 0;
      return emptyArray && noInternal;
    }
    const noValue = value == null || value === '';
    const hasNoInternalSelection = !this.selectedItem;
    return noValue && hasNoInternalSelection;
  }

  resetSelectedItems() {
    // Defer to next tick to avoid NG0100 error in Angular 21+
    this.win.setTimeout(() => {
      if (this.viewDestroyed) {
        return;
      }
      // WI 32158: re-derive the display from the live control value (see
      // deriveSelectedItems) so a reset firing spuriously on a relayout pass
      // does not blank a typeahead whose form value is still set, and does not
      // clobber a hydrated object selection when the control holds a scalar id.
      const value = this.group?.controls?.[this.config?.formControlName]?.value;
      const vp = this.config?.valueProperty;
      const derived = deriveSelectedItems(value, this.selectedItem, vp);
      this.selectedItem = derived.selectedItem;
      this.selectedItems = derived.selectedItems;
      this.cdr?.detectChanges();
    });
  }

  /** Task 35036: a body deferred to the next tick must not refresh a destroyed view. */
  private detectChangesIfAlive() {
    if (!this.viewDestroyed) {
      this.cdr?.detectChanges();
    }
  }

  clear() {
    // this helps ensure the dropdown resets back to placeholder
    this.setValue(null);
  }

  setValue(value: unknown) {
    console.log(
      'component implementations can set component values by overriding setValue:',
      value
    );
  }

  /* eslint-disable */
  itemDisabled(itemArgs: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    dataItem: { Name?: string; Id?: string };
    index: number;
  }) {
    return !itemArgs || itemArgs?.dataItem?.Name === this.config?.placeholder;
  }
  /* eslint-enable */

  onBlur() {
    this.touchedCallback();
  }

  writeValue(value: SelectedItemType) {
    if (value !== this.selectedItem) {
      // Defer to next tick to avoid NG0100 error in Angular 21+
      this.win.setTimeout(() => {
        this.selectedItem = value;
        this.selectedItems = value ? [value] : [];
        this.detectChangesIfAlive();
      });
    }
  }

  registerOnChange(fn: (_: unknown) => void) {
    this.changeCallback = fn;
  }

  registerOnTouched(fn: () => void) {
    this.touchedCallback = fn;
  }

  // #34282: each request carries its own reply scope, so overlaps cannot restamp it
  handleFilterRequest(result: TypeaheadFilterRequest) {
    if (result.initWithQuery) {
      // used to reinit selected value(s) in the control (when already visible on screen)
      // first reset any currently selected items to completely reinit them
      this.resetSelectedItems();
      this.config.options ??= {};
      this.valueObjectId = this.config.options.initWithQuery =
        result.initWithQuery;
      this.initCheckValue();
    } else {
      this.isLoading = true;
      this.filterChangeItem({
        query: result.query,
        replyScope: result.scope,
      }).then(() => {
        this.isLoading = false;
      });
    }
  }

  filterChangeItem($event, valueQueryParamName: string = null) {
    // filterChangeItem can by called by dynamic-.. component where it controls isLoading explicitly,
    // but also can be called as callback of seFilterChangeEx where we control the isLoading here;
    let isLoadingWasSetHere = false;

    if (!this.isLoading) {
      this.isLoading = true;
      isLoadingWasSetHere = true;
    }

    const setNotLoading = () =>
      (this.isLoading = isLoadingWasSetHere ? false : this.isLoading);

    return new Promise((resolve) => {
      this.ngZone.run(() => {
        const scoped = dynamicScopeDefaultQuery(
          this.group,
          this.config.options?.scopeQueryWhenFormControlIsSet
        );
        let scopeDefaultQuery = scoped.query;
        const scopeDefaultQueryParamName = scoped.paramName;

        const deliver = (data: Array<unknown>) => {
          const mappedData = this.filterData(
            data,
            scopeDefaultQuery || $event.query
          );
          $event.extension?.applyData(mappedData);
          this.emitTypeaheadData(mappedData, $event.replyScope);
          setNotLoading();
          resolve(mappedData);
        };
        if (this.config?.options?.dataResolver) {
          this.config?.options?.dataResolver().then(
            (data: Array<unknown>) => {
              this.searchError &&= false;
              deliver(data);
            },
            (err) => {
              if (!dynamicLookupGateEnabled(this.group)) throw err;
              this.reportSearchFailure(err, $event, true);
              setNotLoading();
              resolve([]);
            }
          );
        } else if (this.config?.options?.dropdownOptions) {
          deliver(this.config?.options?.dropdownOptions);
        } else {
          // Remote search path. Sequence so only the latest request drives form state.
          // Do not clear searchError at start: an in-flight retry after a failure should
          // keep the flag until that latest request succeeds (or fails).
          // ?? 0 so Object.create harnesses (and any path that skips field initializers)
          // cannot NaN the seq and silently no-op every guard (NaN === NaN is false).
          if (!this.searchQuery && dynamicLookupGateEnabled(this.group)) {
            return deliver([]); // nothing wired, so never stay "loading" (WI 35277)
          }
          const seq = (this._searchSeq = (this._searchSeq ?? 0) + 1);
          // Prefill / reply-scope lookups paint nothing, so they never supersede a keystroke.
          const listSeq = $event.extension
            ? (this._listSeq = (this._listSeq ?? 0) + 1)
            : 0;
          const params = dynamicSearchParams(
            {
              query: scopeDefaultQuery,
              paramName: scopeDefaultQueryParamName,
            },
            this.customQueryParams,
            { paramName: valueQueryParamName, query: $event.query }
          );
          if (scopeDefaultQuery && scopeDefaultQueryParamName) {
            scopeDefaultQuery = '';
          }
          // Per-subscription: catchError still empties into subscribe via of([]), so we must
          // not clear searchError on that path (would undo the flag set in catchError).
          let failedThisResponse = false;
          this.searchQuery?.(scopeDefaultQuery || $event.query, params)
            .pipe(
              map(this.apiModelMapper.bind(this)),
              catchError((err) => {
                failedThisResponse = true;
                this.reportSearchFailure(
                  err,
                  $event,
                  seq === this._searchSeq,
                  !listSeq || listSeq === this._listSeq
                );
                return of([]);
              })
            )
            .subscribe((data: Array<unknown>) => {
              // Clear failure only on a real success of the latest search (incl. empty list).
              if (seq === this._searchSeq && !failedThisResponse) {
                this.searchError = false;
              }
              if (listSeq && listSeq !== this._listSeq) {
                // Superseded keystroke: settle loading and the promise, never repaint the list.
                setNotLoading();
                return resolve(data);
              }
              const shouldAlwaysClientFilter =
                this.config?.options?.alwaysApplyQueryFilter === true;
              if (
                (!this.win.navigator.onLine || shouldAlwaysClientFilter) &&
                $event.query &&
                this.queryFilter
              ) {
                // when clients are offline the query won't filter remotely so filter on the full cached offline result
                // some typeaheads also opt into always filtering locally to ensure strict results
                data = data.filter((d) => {
                  return this.queryFilter($event.query, d);
                });
              }
              if ($event.extension) {
                $event.extension.applyData(data);
              }
              this.emitTypeaheadData(data, $event.replyScope);

              const candidate = this.singlePreSelectCandidate(data);
              if (candidate && this.hasNoSelection()) {
                const item = candidate as SelectedItemType;
                this.valueChangeItem(item);
                this.updateValue(
                  this.config?.options?.isMultiSelect ? [item] : item
                );
              }

              setNotLoading();

              resolve(data);
            });
        }
      });
    });
  }

  /** Log a failed lookup; flag the control only if latest; empty the list only if it owns it (36106). */
  private reportSearchFailure(
    err: unknown,
    $event: { extension?: { applyData: (d: Array<unknown>) => void } },
    isLatest: boolean,
    isLatestList = true
  ) {
    // Always log: a superseded failure is still a real failure (observability).
    console.error(
      `[dynamic-typeahead] search failed formControlName=${
        this.config?.formControlName ?? '(unknown)'
      }`,
      err
    );
    if (isLatest) {
      this.searchError = true;
      this.searchFailed.emit(err);
    }
    if (isLatestList) {
      $event.extension?.applyData([]);
    }
  }

  valueChangeItems(items: Array<SelectedItemType>) {
    this.selectedItems = items;
    if (items && items.length) {
      this.valueChangeItem(items[0]);
    } else {
      this.valueChangeItem(null);
    }
  }

  /** @param origin 'user' (default — a user selection, marks the value sticky) | 'auto' (an auto-default push) */
  valueChangeItem(item: SelectedItemType, origin: 'user' | 'auto' = 'user') {
    if (origin === 'user') {
      this.retirePrefill();
    }
    item = dynamicUnwrapSelection(item);
    // Defer to next tick to avoid NG0100 error in Angular 21+
    this.win.setTimeout(() => {
      this._setSelectedItem(item, origin);
      this.detectChangesIfAlive();
    });
  }

  onMultiSelectCheckboxChange() {
    if (!this.isMultiSelect) {
      if (this.selectedItems && this.selectedItems.length) {
        this.valueChangeItem(this.selectedItems[0]);
      } else {
        this.valueChangeItem(null);
      }
    }
  }

  // WI 34928: a multi-select auto-default adds the option once and writes the whole selection.
  protected _applyMultiSelectAutoDefault(valueMatch: SelectedItemType) {
    this.selectedItems = dynamicWithPick(
      this.selectedItems,
      valueMatch,
      this._matchingTargetProperty
    );
    this.updateValue([...this.selectedItems]);
    this.valuesChange.emit(this.selectedItems);
  }

  removeItem(item: unknown, property: string) {
    if (this.isDisabled()) {
      return;
    }
    if (this.config?.options?.isMultiSelect) {
      const currentLength = this.selectedItems.length;
      // WI 34928: match by the id the save reads, not the display name, so same-name employees stay apart.
      this.selectedItems = this.selectedItems.filter(
        (val) => !dynamicSameRecord(val, item, this.config, property)
      );
      this.remove.emit(item);
      if (this.selectedItems.length < currentLength) {
        this.valuesChange.emit(this.selectedItems);
        // WI 34928: the control holds the whole selection, not just the chips.
        this.updateValue([...this.selectedItems]);
      }
    } else {
      this.valueChangeItem(null);
    }
    if (!this.selectedItems || this.selectedItems?.length === 0) {
      this.updateValue(null);
      if (this.config?.required) {
        // when clearing out, always reset validity if needed
        this.setRequired();
      }
    }
  }

  private emitTypeaheadData(data: Array<unknown>, replyScope?: string) {
    this.eventBus.emit(
      this.eventBus.types.updateTypeaheadData,
      createTypeaheadDataEvent(
        this.config?.formControlName,
        data,
        this.config?.type,
        replyScope
      )
    );
  }

  private _setSelectedItem(
    item: SelectedItemType,
    origin: 'user' | 'auto' = 'user'
  ) {
    this.selectedItem = item;
    const pick = ++this._fanOutPick;
    // Provenance: a user selection makes the value sticky; an auto-default push does not.
    this._lastValueWasAutoDefault = origin === 'auto';
    if (this.config?.options?.updateOnValueChange) {
      this.config?.options?.updateOnValueChange.forEach((selectionConfig) => {
        if (
          selectionConfig.formControlName &&
          this.selectedItem &&
          !this.selectedItem[this._matchingTargetProperty]
        ) {
          this._runFanOut(selectionConfig, pick);
        } else {
          // WI 35277: no lookup for this pick, so an earlier pick's failure no longer applies
          dynamicSetFanOutError(this._fanOutTarget(selectionConfig), false);
        }
      });

      this._matchingTargetProperty = null;
    }
    if (item) {
      if (this.config.options?.isMultiSelect) {
        if (!this.selectedItems) {
          this.selectedItems = [];
        }

        if (Array.isArray(item)) {
          this.selectedItems = [...item];
        } else {
          const exists = this.selectedItems.includes(item);
          if (!exists) {
            this.selectedItems.push(item);
          }
        }
      } else {
        this.selectedItems = [item];
      }
    } else {
      this.selectedItems = [];
    }

    this.valueChange.emit(item);
    this.valuesChange.emit(this.selectedItems);
  }

  /**
   * Conflict-resolution gate for an incoming auto-default (`valueMatch`). Returns true when it
   * HANDLED the situation (raised a prompt, or chose to silently keep) and the caller must NOT
   * apply the default; false to let the normal apply proceed. See IDynamicConflictResolution.
   */
  private _maybeHandleAutoDefaultConflict(
    valueMatch: SelectedItemType,
    updates: IDynamicModel
  ): boolean {
    const cfg = resolveConflictResolution(this.config);
    if (cfg.mode === 'silent-default') {
      return false; // opt-out / unset => today's always-overwrite behavior
    }
    if (this.config?.options?.isMultiSelect) {
      return false; // conflict resolution applies to single-value typeaheads
    }
    const current = this.selectedItem as unknown as Record<
      string,
      unknown
    > | null;
    const match = valueMatch as unknown as Record<string, unknown> | null;
    const idProp =
      this.config?.valueProperty ||
      (match && Object.prototype.hasOwnProperty.call(match, 'Id')
        ? 'Id'
        : 'id');
    const currentId = current?.[idProp]?.toString()?.toLowerCase();
    if (!current || currentId == null || currentId === '') {
      return false; // nothing sticky to protect
    }
    if (this._lastValueWasAutoDefault) {
      return false; // the current value itself was auto-filled — free to replace silently
    }
    const defaultId = match?.[idProp]?.toString()?.toLowerCase();
    if (currentId === defaultId) {
      return false; // same value — no conflict
    }
    // From here the sticky current value differs from the incoming default.
    const defaultIsEmpty = match == null;

    if (defaultIsEmpty) {
      // The new source selection has no default for this target.
      if (cfg.mode === 'silent-keep' || cfg.emptyDefault === 'keep') {
        return true; // keep the sticky value, no UI
      }
      if (cfg.emptyDefault === 'clear') {
        return false; // let the normal flow clear it (setNullValueIfNotFoundInSearch)
      }
      // cfg.emptyDefault === 'prompt' (mode 'prompt'): offer Keep vs Clear.
      this._raiseConflict(cfg, updates, this.selectedItem, null, true);
      return true;
    }

    if (cfg.mode === 'silent-keep') {
      return true; // keep the sticky value, no UI
    }
    // mode === 'prompt': stash the default and raise the conflict for the user to resolve.
    this._raiseConflict(cfg, updates, this.selectedItem, valueMatch, false);
    return true;
  }

  private _raiseConflict(
    cfg: ReturnType<typeof resolveConflictResolution>,
    updates: IDynamicModel,
    current: SelectedItemType,
    defaultValue: SelectedItemType | null,
    defaultIsEmpty: boolean
  ) {
    this._pendingConflictActive = true;
    this._pendingConflictDefault = defaultValue;
    // capture the original sticky value (before any switch) so "Keep" can restore it later
    this._pendingConflictCurrent = current;
    if (cfg.required) {
      this.conflictPending = true;
    }
    const src = updates.options?.conflictSource;
    const sourceLabels = liveConflictSourceLabels(
      this.dynamicService?.conflictSourceMetaFor(this.config.formControlName),
      this.dynamicService?.activeForm ?? this.group
    );
    const state: IDynamicConflictState = {
      targetFormControlName: this.config.formControlName,
      targetLabel: this.config.label,
      valueLabel: cfg.valueLabel,
      required: cfg.required,
      status: 'unresolved',
      sourceFormControlName: src?.formControlName,
      sourceLabel: src?.label,
      sourceValueDisplay: src?.valueDisplay,
      sourceLabels: sourceLabels.length ? sourceLabels : undefined,
      defaultDisplay: defaultIsEmpty ? '' : cfg.displayValue(defaultValue),
      currentDisplay: cfg.displayValue(current),
      defaultIsEmpty,
    };
    this.dynamicService?.raiseConflict(state);
  }

  /** Reset conflict provenance/pending state and clear any raised conflict (for this target). */
  private _resetConflictState() {
    this._pendingConflictActive = false;
    this._pendingConflictDefault = null;
    this._pendingConflictCurrent = null;
    this._lastValueWasAutoDefault = false;
    this.conflictPending = false;
    if (this.config?.options?.conflictResolution) {
      this.dynamicService?.clearConflict(this.config.formControlName);
    }
  }
}
